import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { useAuth } from "@/features/auth/state/useAuth";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";

import { formatApprovalLabel } from "../approval-utils";
import {
    approveApprovalRequest,
    cancelApprovalRequest,
    getApprovalQueueDetails,
    rejectApprovalRequest,
} from "../api/approvalQueueApi";
import type { ApprovalStatus } from "../types";

const statusPillClassNames: Record<ApprovalStatus, string> = {
    PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    REJECTED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    CANCELLED: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    EXECUTION_FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";

    return new Date(value).toLocaleString("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

const buildActorLabel = (name: string | null | undefined, role: string | null | undefined) => {
    if (!name) return "—";
    return role ? `${name} (${formatApprovalLabel(role)})` : name;
};

export const ApprovalQueueDetailsRoute = () => {
    const { approvalId } = useParams<{ approvalId: string }>();
    const location = useLocation();
    const { user } = useAuth();
    const { hasPermission } = usePermissions();
    const queryClient = useQueryClient();
    const [note, setNote] = useState("");
    const initialFeedback = (location.state as
        | { feedback?: { type: "success" | "error"; text: string } }
        | undefined)?.feedback;
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(initialFeedback ?? null);

    const {
        data,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["approval-queue-detail", approvalId],
        queryFn: () => getApprovalQueueDetails(approvalId!),
        enabled: Boolean(approvalId),
    });

    const request = data?.data.request;
    const payload = data?.data.payload;
    const isPending = request?.status === "PENDING";
    const isInitiator = request?.initiatedBy?.adminId === user?.userId;
    const canManageApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS_MANAGE[0]);
    const canApproveOrReject = Boolean(isPending && canManageApprovals && !isInitiator);
    const canCancel =
        Boolean(isPending) && (Boolean(isInitiator) || canManageApprovals);

    const emailHtml = useMemo(() => {
        if (!payload || typeof payload !== "object") return null;

        const candidate = (payload as Record<string, unknown>).emailHtml;
        return typeof candidate === "string" && candidate.trim() ? candidate : null;
    }, [payload]);

    const sharedMutationOptions = {
        onSuccess: (response: { message: string }) => {
            setFeedback({
                type: "success",
                text: response.message,
            });
            setNote("");
            queryClient.invalidateQueries({ queryKey: ["approval-queue"] });
            queryClient.invalidateQueries({
                queryKey: ["approval-queue-detail", approvalId],
            });
        },
        onError: (mutationError: unknown) => {
            setFeedback({
                type: "error",
                text: extractApiError(mutationError),
            });
        },
    };

    const approveMutation = useMutation({
        mutationFn: () =>
            approveApprovalRequest(approvalId!, note.trim() ? { note: note.trim() } : undefined),
        ...sharedMutationOptions,
    });

    const rejectMutation = useMutation({
        mutationFn: () =>
            rejectApprovalRequest(approvalId!, note.trim() ? { note: note.trim() } : undefined),
        ...sharedMutationOptions,
    });

    const cancelMutation = useMutation({
        mutationFn: () =>
            cancelApprovalRequest(approvalId!, note.trim() ? { note: note.trim() } : undefined),
        ...sharedMutationOptions,
    });

    const isMutating =
        approveMutation.isPending ||
        rejectMutation.isPending ||
        cancelMutation.isPending;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !request) {
        return (
            <div className="space-y-4">
                <Link
                    to="/approval-queue"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Approval Queue
                </Link>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300">
                    {error ? extractApiError(error) : "Approval request could not be loaded."}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Link
                to="/approval-queue"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Approval Queue
            </Link>

            {feedback ? (
                <div
                    className={`rounded-2xl px-4 py-3 text-sm ${
                        feedback.type === "success"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                            : "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300"
                    }`}
                >
                    {feedback.text}
                </div>
            ) : null}

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusPillClassNames[request.status]}`}
                            >
                                {formatApprovalLabel(request.status)}
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {formatApprovalLabel(request.actionType)}
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {formatApprovalLabel(request.targetType)}
                            </span>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                                {request.summary}
                            </h1>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Approval ID:{" "}
                                <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
                                    {request.approvalId}
                                </span>
                            </p>
                        </div>
                        {request.requestReason ? (
                            <p className="max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                                {request.requestReason}
                            </p>
                        ) : null}
                    </div>
                    {request.status === "PENDING" ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                            This request is waiting for a super admin decision before the action
                            executes.
                        </div>
                    ) : null}
                </div>
            </section>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
                <section className="space-y-6">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Request Metadata
                        </h2>
                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Initiated By
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {buildActorLabel(
                                        request.initiatedBy?.name,
                                        request.initiatedBy?.role
                                    )}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Created At
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {formatDateTime(request.createdAt)}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Decision Note
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {request.decisionNote || "—"}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Executed At
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {formatDateTime(request.executedAt)}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Approved By
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {buildActorLabel(
                                        request.approvedBy?.name,
                                        request.approvedBy?.role
                                    )}
                                </p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/40">
                                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                                    Rejected / Cancelled By
                                </p>
                                <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {buildActorLabel(
                                        request.rejectedBy?.name ?? request.cancelledBy?.name,
                                        request.rejectedBy?.role ?? request.cancelledBy?.role
                                    )}
                                </p>
                            </div>
                        </div>
                        {request.executionError ? (
                            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300">
                                <div className="flex items-start gap-2">
                                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                    <div>
                                        <p className="font-semibold">Execution Error</p>
                                        <p className="mt-1">{request.executionError}</p>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {emailHtml ? (
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="flex items-center gap-2">
                                <ClockIcon className="h-5 w-5 text-primary-500" />
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                    Email Preview
                                </h2>
                            </div>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                This is the sanitized HTML that will be delivered if the request is
                                approved.
                            </p>
                            <iframe
                                title="Approval email preview"
                                srcDoc={emailHtml}
                                className="mt-4 h-[560px] w-full rounded-2xl border border-slate-200 bg-white dark:border-slate-700"
                            />
                        </div>
                    ) : null}

                </section>

                <aside className="space-y-6">
                    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Decision Controls
                        </h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Add an optional note to explain the approval, rejection, or cancellation.
                        </p>
                        <div className="mt-4 space-y-4">
                            <textarea
                                rows={5}
                                value={note}
                                onChange={(event) => setNote(event.target.value)}
                                placeholder="Optional decision note..."
                                disabled={!isPending || isMutating}
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 disabled:cursor-not-allowed disabled:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:disabled:bg-slate-900"
                            />
                            {!isPending ? (
                                <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                    This request is no longer pending, so no further decision action
                                    is available.
                                </div>
                            ) : null}
                            {isInitiator && canManageApprovals ? (
                                <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                                    You initiated this request, so you cannot approve or reject it
                                    yourself. You can still cancel it while it is pending.
                                </div>
                            ) : null}
                        </div>
                        <div className="mt-6 space-y-3">
                            {canApproveOrReject ? (
                                <>
                                    <Button
                                        onClick={() => approveMutation.mutate()}
                                        disabled={isMutating}
                                        className="w-full justify-center gap-2"
                                    >
                                        <CheckCircleIcon className="h-4 w-4" />
                                        {approveMutation.isPending
                                            ? "Approving..."
                                            : "Approve Request"}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => rejectMutation.mutate()}
                                        disabled={isMutating}
                                        className="w-full justify-center gap-2 border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900/50 dark:text-rose-300 dark:hover:bg-rose-900/20"
                                    >
                                        <XCircleIcon className="h-4 w-4" />
                                        {rejectMutation.isPending
                                            ? "Rejecting..."
                                            : "Reject Request"}
                                    </Button>
                                </>
                            ) : null}
                            {canCancel ? (
                                <Button
                                    variant="secondary"
                                    onClick={() => cancelMutation.mutate()}
                                    disabled={isMutating}
                                    className="w-full justify-center gap-2"
                                >
                                    <NoSymbolIcon className="h-4 w-4" />
                                    {cancelMutation.isPending
                                        ? "Cancelling..."
                                        : "Cancel Request"}
                                </Button>
                            ) : null}
                        </div>
                    </section>
                </aside>
            </div>
        </div>
    );
};
